import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  cancelCreatorEvent,
  closeCreatorEventSales,
  creatorEventExportUrl,
  listCreatorEvents,
  openCreatorEventSales,
  type CreatorEventRow,
} from '../../../api/creatorEvents'
import { SettingsButton, SettingsCard, SettingsTitle } from '../../../components/settings/SettingsUI'
import ConfirmModal from './ConfirmModal'
import RowActionsMenu from './RowActionsMenu'

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export function statusLabel(status: string) {
  switch (status) {
    case 'draft':
      return 'Draft'
    case 'upcoming':
      return 'Upcoming'
    case 'live':
      return 'Live'
    case 'past':
      return 'Past'
    case 'cancelled':
      return 'Cancelled'
    case 'sold_out':
      return 'Sold out'
    default:
      return status
  }
}

export function statusBadges(row: CreatorEventRow) {
  const out: Array<{ key: string; label: string; cls: string }> = []
  if (row.event_is_cancelled) {
    out.push({ key: 'cancelled', label: 'Cancelled', cls: 'border-red-500/40 bg-red-500/15 text-red-200' })
  } else {
    if (row.event_sales_closed) {
      out.push({ key: 'sales_closed', label: 'Sales closed', cls: 'border-amber-500/35 bg-amber-500/15 text-amber-200' })
    }
    if (row.event_is_ticketed && row.tickets_remaining === 0) {
      out.push({ key: 'sold_out', label: 'Sold out', cls: 'border-fuchsia-500/35 bg-fuchsia-500/15 text-fuchsia-200' })
    }
    if (out.length < 2) {
      out.push({ key: `status-${row.status}`, label: statusLabel(row.status), cls: 'border-white/20 bg-white/10 text-white/90' })
    }
  }
  return out.slice(0, 2)
}

export function filterEvents(rows: CreatorEventRow[], query: string) {
  const q = query.trim().toLowerCase()
  if (!q) return rows
  return rows.filter((r) => (r.title || '').toLowerCase().includes(q))
}

type ConfirmState =
  | { kind: 'cancel'; row: CreatorEventRow }
  | { kind: 'closeSales'; row: CreatorEventRow }
  | null

export default function CreatorToolsPixeEvents() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<CreatorEventRow[]>([])
  const [metrics, setMetrics] = useState({
    upcoming_events: 0,
    tickets_sold_30d: 0,
    tickets_sold_all: 0,
    gross_pixe: 0,
    fee_pixe: 0,
    net_pixe: 0,
  })
  const [query, setQuery] = useState('')
  const [confirm, setConfirm] = useState<ConfirmState>(null)
  const [working, setWorking] = useState(false)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await listCreatorEvents()
      setRows(data.results || [])
      setMetrics(data.metrics || metrics)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const filtered = useMemo(() => filterEvents(rows, query), [rows, query])

  async function runConfirm() {
    if (!confirm) return
    setWorking(true)
    try {
      if (confirm.kind === 'cancel') {
        await cancelCreatorEvent(confirm.row.id)
      } else if (confirm.kind === 'closeSales') {
        await closeCreatorEventSales(confirm.row.id)
      }
      setConfirm(null)
      await load()
    } catch (e: any) {
      window.alert(e?.response?.data?.detail || e?.message || 'Action failed')
    } finally {
      setWorking(false)
    }
  }

  async function onOpenSales(row: CreatorEventRow) {
    try {
      await openCreatorEventSales(row.id)
      await load()
    } catch (e: any) {
      window.alert(e?.response?.data?.detail || e?.message || 'Action failed')
    }
  }

  return (
    <div className="space-y-4">
      <SettingsTitle
        title="Pixe Events"
        subtitle="Manage your events, ticket sales, attendees, and exports."
        right={
          <Link
            to="/publish/new?format=event"
            className="inline-flex h-9 items-center justify-center rounded-lg px-3 text-sm font-semibold transition border bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50"
          >
            Create Event
          </Link>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <SettingsCard title="Upcoming" subtitle="Scheduled/live/sold out events">
          <div className="text-2xl font-extrabold">{metrics.upcoming_events}</div>
        </SettingsCard>
        <SettingsCard title="Tickets sold (30d)">
          <div className="text-2xl font-extrabold">{metrics.tickets_sold_30d}</div>
        </SettingsCard>
        <SettingsCard title="Gross">
          <div className="text-2xl font-extrabold">{metrics.gross_pixe} PIXE</div>
        </SettingsCard>
        <SettingsCard title="Platform fees">
          <div className="text-2xl font-extrabold">{metrics.fee_pixe} PIXE</div>
        </SettingsCard>
        <SettingsCard title="Net revenue">
          <div className="text-2xl font-extrabold">{metrics.net_pixe} PIXE</div>
        </SettingsCard>
      </div>

      <SettingsCard
        title="My Events"
        subtitle="Draft, upcoming, live, and past events you own."
        right={<SettingsButton variant="ghost" onClick={() => void load()}>Refresh</SettingsButton>}
      >
        <div className="mb-3 flex items-center gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events"
            className="w-full max-w-sm rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/30"
          />
        </div>
        {loading ? <div className="text-sm text-white/70">Loading events…</div> : null}
        {error ? <div className="text-sm text-red-300">{error}</div> : null}
        {!loading && !error ? (
          filtered.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-white/65">
                    <th className="px-2 py-2">Event</th>
                    <th className="px-2 py-2">Start</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Sold/Capacity</th>
                    <th className="px-2 py-2">Gross/Net</th>
                    <th className="px-2 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((row) => (
                    <tr key={row.id} className="border-t border-white/10">
                      <td className="px-2 py-1.5 font-semibold">{row.title || `Event #${row.id}`}</td>
                      <td className="px-2 py-1.5">{fmtDate(row.event_starts_at)}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex flex-wrap gap-1.5">
                          {statusBadges(row).map((b) => (
                            <span key={b.key} className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${b.cls}`}>{b.label}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-1.5">{row.tickets_sold}/{row.event_capacity ?? '—'}</td>
                      <td className="px-2 py-1.5">{row.gross_pixe} / {row.net_pixe} PIXE</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          <Link to={`/p/${row.id}`} className="rounded border border-white/15 px-2 py-1 text-xs hover:bg-white/10">View</Link>
                          <Link to={`/settings/creator-tools/events/${row.id}`} className="rounded border border-white/15 px-2 py-1 text-xs hover:bg-white/10">Manage</Link>
                          <RowActionsMenu
                            actions={[
                              { key: 'edit', label: 'Edit', onClick: () => navigate(`/publish/new?draft=${row.id}`) },
                              { key: 'export_orders', label: 'Export Orders CSV', onClick: () => { window.location.href = creatorEventExportUrl(row.id, 'orders') } },
                              { key: 'export_attendees', label: 'Export Attendees CSV', onClick: () => { window.location.href = creatorEventExportUrl(row.id, 'attendees') } },
                              row.event_sales_closed
                                ? { key: 'open_sales', label: 'Open sales', onClick: () => { void onOpenSales(row) } }
                                : { key: 'close_sales', label: 'Close sales', onClick: () => setConfirm({ kind: 'closeSales', row }) },
                              ...(row.event_is_cancelled ? [] : [{ key: 'cancel', label: 'Cancel event', danger: true, onClick: () => setConfirm({ kind: 'cancel', row }) }]),
                            ]}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-white/65">No events found.</div>
          )
        ) : null}
      </SettingsCard>

      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.kind === 'cancel' ? 'Cancel event?' : 'Close ticket sales?'}
        body={
          confirm?.kind === 'cancel'
            ? 'This stops ticket sales immediately. Refunds are not automatic yet.'
            : 'You can reopen sales later from event settings.'
        }
        confirmLabel={confirm?.kind === 'cancel' ? 'Cancel event' : 'Close sales'}
        cancelLabel={confirm?.kind === 'cancel' ? 'Keep event' : 'Keep open'}
        danger={confirm?.kind === 'cancel'}
        busy={working}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void runConfirm()}
      />
    </div>
  )
}
